import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LineChart } from 'components/LineChart';
import { ROUTES } from 'constants/routes';
import { useBarometerChartData } from 'pages/WellBeingBarometer/useBarometerChartData';
import {
  calculateBarometerYDomain,
  createBarometerCategories,
  formatBarometerTooltipValue,
  formatBarometerXAxisLabel,
  formatBarometerYAxisLabel,
  getBarometerCategoryLabel,
} from 'pages/WellBeingBarometer/utils';

interface BarometerCardProps {
  isTestCompleted: boolean;
  onStartTest: () => void;
  onNoData?: () => void;
}

export const BarometerCard = ({
  isTestCompleted,
  onNoData,
  onStartTest,
}: BarometerCardProps): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const [isStarting, setIsStarting] = useState(false);
  const { data: chartData, isLoading, error } = useBarometerChartData();
  const hasChartData = chartData && chartData.length > 0;
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !hasChartData && onNoData) {
      onNoData();
    }
  }, [isLoading, hasChartData, onNoData]);

  const handleStartTest = (): void => {
    if (isStarting) return;
    setIsStarting(true);
    onStartTest();
  };

  const handleCompleted = (): void => {
    navigate(ROUTES.PAE);
  };

  const handleOnClick = (): void => {
    if (isTestCompleted) {
      handleCompleted();
    } else {
      handleStartTest();
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6 w-full">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">{t('Common.loading', { ns: LOCIZE_NS.COMMON })}</p>
        </div>
      </div>
    );
  }

  if (!hasChartData) {
    return null;
  }

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center bg-teal-100">
          <TrendingUp className="w-5 h-5 text-teal-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{t('BarometerResults.heading')}</h3>
      </div>
      <div className="mb-4 w-full">
        <LineChart
          data={chartData}
          categories={createBarometerCategories(
            (key: string) => t(`BarometerResults.Areas.${key}`) || key
          )}
          xAxisKey="label"
          isLoading={isLoading}
          error={error}
          formatXAxisLabel={formatBarometerXAxisLabel}
          formatYAxisLabel={formatBarometerYAxisLabel}
          getCategoryLabel={(category: string) => getBarometerCategoryLabel(t, category)}
          calculateYDomain={calculateBarometerYDomain}
          formatTooltipLabel={formatBarometerTooltipValue}
        />
      </div>
      <Button className="w-full" onClick={handleOnClick}>
        {isTestCompleted ? t('WellBeing.Test.cancel') : t('WellBeing.Test.start.cta')}
      </Button>
    </Card>
  );
};
